import { FileText, Shield, Download, ArrowRight, Heart, Sparkles } from 'lucide-react'
import Header from './Header'

interface Props {
  onGetStarted: () => void
}

export default function LandingPage({ onGetStarted }: Props) {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" />
        <div className="absolute top-40 right-10 w-72 h-72 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }} />

        <div className="relative max-w-4xl mx-auto px-4 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Free to use, no account needed
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Help your nanny truly
            <span className="text-emerald-600"> understand </span>
            your child
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10">
            Turn diagnostic reports into a clear, friendly briefing pack
            that helps nannies provide the best possible care.
          </p>
          <button
            onClick={onGetStarted}
            className="group inline-flex items-center gap-2 bg-emerald-600 text-white px-8 py-4 rounded-full font-medium hover:bg-emerald-700 transition-all hover:shadow-lg hover:shadow-emerald-200 hover:-translate-y-0.5"
          >
            Create your briefing pack
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Hero Image */}
      <section className="relative -mt-8 mb-16">
        <div className="max-w-4xl mx-auto px-4">
          <img
            src="/hero.png"
            alt="A nanny and child together"
            className="w-full rounded-2xl shadow-2xl"
          />
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          <p className="text-xl text-gray-600 leading-relaxed text-center">
            If your child has a neurodevelopmental difference — whether that's autism, ADHD,
            dyspraxia, sensory processing differences, or any other profile — you know how
            much effort goes into explaining their needs to new caregivers. This tool takes
            the key information from your child's reports and turns it into something
            practical: <strong className="text-gray-900">a briefing pack your nanny can actually use</strong>.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            How it works
          </h2>
          <p className="text-gray-600 text-center mb-16 max-w-xl mx-auto">
            Three simple steps to create a professional briefing pack
          </p>

          <div className="grid md:grid-cols-3 gap-8 md:gap-6">
            <div className="relative bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="absolute -top-4 left-8 w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                1
              </div>
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-5">
                <FileText className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-gray-900 text-lg mb-3">Upload your report</h3>
              <p className="text-gray-600">
                Upload your child's diagnostic assessment, EHCP, therapy report, or clinical
                letter. We extract only what's written there — nothing more.
              </p>
            </div>

            <div className="relative bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="absolute -top-4 left-8 w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                2
              </div>
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-5">
                <Heart className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-gray-900 text-lg mb-3">Add your insights (optional)</h3>
              <p className="text-gray-600">
                Tell us what makes your child light up, and share any strategies that work
                at home. You know them best.
              </p>
            </div>

            <div className="relative bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="absolute -top-4 left-8 w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                3
              </div>
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-5">
                <Download className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-gray-900 text-lg mb-3">Get your briefing pack</h3>
              <p className="text-gray-600">
                We generate a clear, friendly summary you can download as a PDF
                and share with any nanny or caregiver.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What you'll get */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            What's in a briefing pack?
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              'A snapshot of your child — how they experience the world, in plain language',
              'Their strengths and what makes them shine',
              'Challenges and triggers to be aware of',
              'Practical strategies that usually help',
              'Things to avoid that can make situations worse',
              'Questions for nannies to ask you, and an onboarding checklist'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full" />
                </div>
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-14 h-14 bg-emerald-600 rounded-2xl flex items-center justify-center shrink-0">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-xl mb-3">
                  Your privacy matters
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We don't store your child's reports. Your document is processed securely
                  to generate the briefing pack, then immediately discarded. The briefing
                  pack exists only in your browser until you download it. We'll explain
                  exactly how your data is handled before you upload anything.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-teal-600">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to create your briefing pack?
          </h2>
          <p className="text-emerald-100 text-lg mb-10">
            It takes about 5 minutes. You'll need at least one letter or report.
          </p>
          <button
            onClick={onGetStarted}
            className="group inline-flex items-center gap-2 bg-white text-emerald-700 px-8 py-4 rounded-full font-semibold hover:bg-emerald-50 transition-all hover:shadow-lg hover:-translate-y-0.5"
          >
            Get started now
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <img src="/logo.svg" alt="Koru Kids" className="h-8 w-auto mx-auto mb-4 brightness-0 invert opacity-80" />
          <p className="text-gray-400 text-sm">
            Made by{' '}
            <a
              href="https://www.korukids.co.uk"
              className="text-emerald-400 hover:text-emerald-300 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              Koru Kids
            </a>
            {' '}to help families share what matters most.
          </p>
        </div>
      </footer>
    </div>
  )
}
